import React from 'react';
import { DashboardFilter } from '../../types';
import { Calendar, Filter, RefreshCw } from 'lucide-react';

interface DashboardFiltersProps {
  filters: DashboardFilter;
  onFiltersChange: (filters: DashboardFilter) => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
}

export function DashboardFilters({ 
  filters, 
  onFiltersChange, 
  onRefresh, 
  isRefreshing 
}: DashboardFiltersProps) {
  const timeRangeOptions = [
    { value: '1h', label: '1 Hour' },
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
  ];

  const refreshIntervalOptions = [
    { value: 5, label: '5 seconds' },
    { value: 15, label: '15 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
  ];

  const handleTimeRangeChange = (timeRange: DashboardFilter['timeRange']) => {
    onFiltersChange({ ...filters, timeRange });
  };

  const handleRefreshIntervalChange = (refreshInterval: number) => {
    onFiltersChange({ ...filters, refreshInterval });
  };

  const handleAppsChange = (apps: string[]) => {
    onFiltersChange({ ...filters, apps });
  };

  const handleStatusChange = (status: ('healthy' | 'degraded' | 'down')[]) => {
    onFiltersChange({ ...filters, status });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
      <div className="flex flex-wrap items-center gap-4">
        {/* Time Range */}
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Time Range:
          </label>
          <select
            value={filters.timeRange}
            onChange={(e) => handleTimeRangeChange(e.target.value as DashboardFilter['timeRange'])}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {timeRangeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Refresh Interval */}
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Refresh:
          </label>
          <select
            value={filters.refreshInterval}
            onChange={(e) => handleRefreshIntervalChange(Number(e.target.value))}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {refreshIntervalOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Status:
          </label>
          <div className="flex space-x-2">
            {(['healthy', 'degraded', 'down'] as const).map((status) => (
              <label key={status} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.status?.includes(status) ?? true}
                  onChange={(e) => {
                    const currentStatus = filters.status || ['healthy', 'degraded', 'down'];
                    if (e.target.checked) {
                      handleStatusChange([...currentStatus.filter(s => s !== status), status]);
                    } else {
                      handleStatusChange(currentStatus.filter(s => s !== status));
                    }
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-1 text-sm text-gray-700 dark:text-gray-300 capitalize">
                  {status}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Apps Filter */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Apps:
          </label>
          <input
            type="text"
            placeholder="Filter by app names..."
            value={filters.apps?.join(', ') || ''}
            onChange={(e) => {
              const apps = e.target.value
                .split(',')
                .map(app => app.trim())
                .filter(app => app.length > 0);
              handleAppsChange(apps);
            }}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
          />
        </div>

        {/* Manual Refresh Button */}
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Active Filters Summary */}
      {(filters.apps?.length || (filters.status && filters.status.length < 3)) && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Active filters:</span>
            {filters.apps?.length && (
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded">
                Apps: {filters.apps.join(', ')}
              </span>
            )}
            {filters.status && filters.status.length < 3 && (
              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded">
                Status: {filters.status.join(', ')}
              </span>
            )}
            <button
              onClick={() => onFiltersChange({ 
                ...filters, 
                apps: undefined, 
                status: undefined 
              })}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
}